import * as React from 'react'
import { useMarkdownString } from './hooks'
import MarkdownTextToSpeech from './TextToSpeechMarkdown'
import { MarkdownTypes } from '@mission.io/question-views'

/**
 * OptionalMarkdown - will render the children as markdown if it is a markdown string (or if interpretAsMarkdown is true)
 *
 * @param {Object} props - the react props
 * @param {string} props.children - the markdown string
 * @param {?Array<string>} props.disabledComponents? - a list of components to refuse to render
 * @param {TextToSpeech} props.textToSpeech? - a component which handles text to speech
 * @param {?MarkdownOverrideBundle} props.overrides? - a mapping of components names to the components which should be used when rendering markdown and text to speech
 * @param {?boolean} props.interpretAsMarkdown? - if true, will interpret the children as markdown. If false, will not interpret the children as markdown. If left unset, will check the children to determine if it is markdown.
 *
 * @return {React$Node}
 */
export default function OptionalMarkdown({
	children,
	disabledComponents,
	textToSpeech: TextToSpeech,
	interpretAsMarkdown,
	overrides,
	className,
}: {
	children: string
	disabledComponents?: Array<string>
	textToSpeech?: React.ComponentType<{
		children: React.ReactNode
		onStartSpeech?: (event: TouchEvent | MouseEvent) => void
	}>
	interpretAsMarkdown?: boolean
	overrides?: MarkdownTypes.MarkdownOverrideBundle
	className?: string
}): JSX.Element {
	const markdownString = useMarkdownString(children)

	if (
		interpretAsMarkdown === true ||
		(markdownString != null && interpretAsMarkdown !== false)
	) {
		return (
			<MarkdownTextToSpeech
				className={className}
				textToSpeech={TextToSpeech}
				disabledComponents={disabledComponents}
				overrides={overrides}>
				{markdownString ?? children}
			</MarkdownTextToSpeech>
		)
	}

	if (TextToSpeech) {
		return <TextToSpeech>{children}</TextToSpeech>
	}

	return <>{children}</>
}
